import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

// Variable global para almacenar el token dinámico para operaciones admin
let currentAdminAuthToken: string | null = null;

// Función para actualizar el token dinámicamente
export const setAdminAuthToken = (token: string | null) => {
    currentAdminAuthToken = token;
};

// Función para obtener el token actual
export const getAdminAuthToken = (): string | null => {
    return currentAdminAuthToken;
};

// URL del BFF de administración desde variable de entorno
let adminGraphqlUrl = import.meta.env.VITE_GRAPHQL_ADMIN;

if (!adminGraphqlUrl) {
    throw new Error('❌ VITE_GRAPHQL_ADMIN no está configurado en las variables de entorno');
}

// Asegurar que tenga protocolo https://
if (!adminGraphqlUrl.startsWith('http://') && !adminGraphqlUrl.startsWith('https://')) {
    adminGraphqlUrl = 'https://' + adminGraphqlUrl;
}

// Siempre agregar /query si no lo tiene
if (!adminGraphqlUrl.endsWith('/query')) {
    adminGraphqlUrl = adminGraphqlUrl.replace(/\/$/, '') + '/query';
}

// Enlace HTTP para operaciones admin
const adminHttpLink = createHttpLink({
    uri: adminGraphqlUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    fetch: (uri, options) => {
        return fetch(uri, options).then(response => {
            return response;
        }).catch(error => {
            console.error('❌ [apollo-admin] Fetch error:', error);
            throw error;
        });
    },
});

// Enlace de contexto para añadir el token JWT en los headers
const adminAuthLink = setContext(async (_, { headers }) => {
    let token = currentAdminAuthToken;

    // Si aún no hay token, esperar a que el frontend padre lo envíe (evita race condition 401)
    if (!token && typeof window !== 'undefined' && (window as any).microfrontendAuth?.waitForToken) {
        try {
            token = await (window as any).microfrontendAuth.waitForToken(5000);
            if (token) currentAdminAuthToken = token;
        } catch {
            // Timeout: proceder sin token (el error link manejará el 401 sin hacer logout)
        }
    }

    return {
        headers: {
            ...headers,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            authorization: token ? `Bearer ${token}` : '',
        }
    };
});

// Enlace de manejo de errores
const adminErrorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(
                `[GraphQL Admin Error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            
            // Detectar errores de autenticación
            if (
                message?.toLowerCase().includes('unauthorized') ||
                message?.toLowerCase().includes('authentication') ||
                message?.toLowerCase().includes('jwt')
            ) {
                const hadToken = !!currentAdminAuthToken;
                console.error('❌ [Apollo Admin] Token expirado o inválido.');
                currentAdminAuthToken = null;
                
                // Solo notificar al padre si teníamos un token válido (no por race condition)
                if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                    (window as any).microfrontendAuth.notifyTokenExpired();
                }
            }
        });
    }

    if (networkError) {
        const statusCode = (networkError as any)?.statusCode;
        if (statusCode === 401 || statusCode === 403) {
            const hadToken = !!currentAdminAuthToken;
            console.error('❌ [Apollo Admin] Error de autenticación (HTTP', statusCode, ')');
            currentAdminAuthToken = null;
            
            // Solo notificar al padre si teníamos un token válido (no por race condition)
            if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                (window as any).microfrontendAuth.notifyTokenExpired();
            }
        }
    }
});

// Cliente Apollo para operaciones admin
const adminApolloClient = new ApolloClient({
    link: adminErrorLink.concat(adminAuthLink.concat(adminHttpLink)),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        mutate: {
            errorPolicy: 'all',
        },
    },
});

export default adminApolloClient;
